import { FileText, Download, Eye, Paperclip, Calendar, Hash, Building, User, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { JOURNAL_TYPES } from '../../config/accounting';
import type { JournalEntry } from '../../types/accounting';
import { useCurrency } from '../../hooks/useCurrency';

interface JournalEntryViewProps {
  entry: JournalEntry;
  onDownloadAttachment?: (attachmentId: string) => void;
  onViewAttachment?: (attachmentId: string) => void;
}

export function JournalEntryView({ entry, onDownloadAttachment, onViewAttachment }: JournalEntryViewProps) {
  const { formatConverted } = useCurrency();
  
  const formatAmount = (amount: number) => {
    return formatConverted(amount);
  };
  
  const getJournalLabel = (type: string) => {
    return JOURNAL_TYPES[type as keyof typeof JOURNAL_TYPES]?.label || type;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted':
        return 'text-green-600 bg-green-100';
      case 'approved':
        return 'text-blue-600 bg-blue-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'posted':
        return <CheckCircle className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'draft':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* En-tête avec informations principales */}
      <Card title="Informations générales" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <Building className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Journal</p>
              <p className="font-medium text-gray-900">{getJournalLabel(entry.journalType)}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium text-gray-900">
                {new Date(entry.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Hash className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">N° Pièce</p>
              <p className="font-medium text-gray-900">{entry.reference}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {getStatusIcon(entry.status)}
            <div>
              <p className="text-sm text-gray-500">Statut</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(entry.status)}`}>
                {entry.status}
              </span>
            </div>
          </div>
          
          {entry.source === 'agent' && (
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-gray-500">Source</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  ADHA AI
                </span>
              </div>
            </div>
          )}
          
          {entry.validationStatus && (
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">Validation</p>
                <p className="font-medium text-gray-900 capitalize">{entry.validationStatus}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4">
          <p className="text-sm text-gray-500">Libellé</p>
          <p className="font-medium text-gray-900">{entry.description}</p>
        </div>
      </Card>

      {/* Tableau des lignes d'écriture */}
      <Card title="Détails comptables" icon={Hash}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TVA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Débit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Crédit
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(Array.isArray(entry.lines) ? entry.lines : []).map((line, idx) => (
                <tr key={line.id || `line-${idx}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {line.accountCode}
                      </div>
                      <div className="text-sm text-gray-500">
                        {line.accountName}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {line.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {line.vatAmount && line.vatAmount > 0 ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {line.vatCode}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatAmount(line.vatAmount)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {line.debit > 0 ? (
                      <span className="text-sm font-medium text-gray-900">
                        {formatAmount(line.debit)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {line.credit > 0 ? (
                      <span className="text-sm font-medium text-gray-900">
                        {formatAmount(line.credit)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={2} className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
                  Totaux
                </td>
                <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                  {entry.totalVat && entry.totalVat > 0 ? formatAmount(entry.totalVat) : '-'}
                </td>
                <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                  {formatAmount(entry.totalDebit)}
                </td>
                <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                  {formatAmount(entry.totalCredit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Pièces justificatives */}
      <Card title="Pièces justificatives" icon={Paperclip}>
        {entry.attachments && entry.attachments.length > 0 ? (
          <div className="space-y-4">
            {entry.attachments.map(attachment => (
              <div 
                key={attachment.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {attachment.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Status: {attachment.status === 'uploaded' ? 'Disponible' : attachment.status}
                    </p>
                  </div>
                </div>
                {attachment.status === 'uploaded' && (
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Eye}
                      onClick={() => onViewAttachment?.(attachment.id)}
                    >
                      Aperçu
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={Download}
                      onClick={() => onDownloadAttachment?.(attachment.id)}
                    >
                      Télécharger
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Paperclip className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Aucune pièce justificative attachée</p>
          </div>
        )}
      </Card>
    </div>
  );
}